import { Button, Input } from "@scribenote/brian-design-system";
import { type RefObject, useEffect, useState } from "react";

import type { RoomVideo } from "./perceptionClient";
import { VIDEO_MS } from "./perceptionQueries";
import { clockWord } from "./words";

export const frameTime = (video: RoomVideo, seconds: number) =>
  new Date(Date.parse(video.startedAt) + seconds * 1000).toISOString();

const clockInput = (ms: number) => new Date(ms).toTimeString().slice(0, 8);

const fromClockInput = (value: string) => {
  const [hours, minutes, seconds = 0] = value.split(":").map(Number);
  return new Date().setHours(hours, minutes, seconds, 0);
};

/** The start of the 2 minutes of recent video shown. */
export const VideoFrom = ({
  from,
  onFrom,
}: {
  from: number;
  onFrom: (ms: number) => void;
}) => {
  const [draft, setDraft] = useState(() => clockInput(from));
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (draft) onFrom(fromClockInput(draft));
      }}
    >
      <Input
        type="time"
        step={1}
        aria-label="Video from"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <Button type="submit" variant="outline" size="sm">
        Show 2 minutes
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          const latest = Date.now() - VIDEO_MS;
          setDraft(clockInput(latest));
          onFrom(latest);
        }}
      >
        Latest
      </Button>
    </form>
  );
};

/**
 * The room's camera as recorded, at its own frame rate rather than the live
 * view's still a second, so what happened can be timed after the fact. The
 * time shown is the frame's, on the browser clock.
 */
export const RecentVideo = ({
  roomName,
  query,
  videoRef,
  aspect,
}: {
  roomName: string;
  query: { data: RoomVideo | null | undefined; isError: boolean };
  videoRef: RefObject<HTMLVideoElement | null>;
  aspect: number;
}) => {
  const [seconds, setSeconds] = useState(0);
  const video = query.data;
  const mp4 = video?.mp4;

  useEffect(() => {
    const element = videoRef.current;
    if (!element || !mp4) return;
    const url = URL.createObjectURL(mp4);
    element.src = url;
    return () => {
      element.removeAttribute("src");
      URL.revokeObjectURL(url);
    };
  }, [mp4, videoRef]);

  return (
    <div
      data-dd-privacy="hidden"
      className="relative w-full overflow-hidden bg-recess"
      style={{ aspectRatio: aspect, width: `min(100cqw, 100cqh * ${aspect})` }}
    >
      {video ? (
        <>
          <video
            ref={videoRef}
            controls
            playsInline
            aria-label={`Recorded video from ${roomName}`}
            onLoadedMetadata={(e) => setSeconds(e.currentTarget.currentTime)}
            onTimeUpdate={(e) => setSeconds(e.currentTarget.currentTime)}
            className="absolute inset-0 h-full w-full object-contain"
          />
          <span className="absolute top-1 left-1 rounded-paper bg-card px-1 font-mono text-fine-print text-ink-body">
            {clockWord(frameTime(video, seconds))}
          </span>
        </>
      ) : (
        <p className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-annotation">
          {query.isError
            ? "I couldn’t load the video."
            : video === null
              ? "No video from this room in those 2 minutes."
              : "Loading the video."}
        </p>
      )}
    </div>
  );
};
